import { defaultMarkingFor, flattenParts, positionIssues } from '@kronos/domain'
import type { PartsEntry, PurchaseLink } from '@kronos/domain'
import type { ComponentKind, ComponentType, Marking, Tracking } from '../../data'

/**
 * The item editor's state, and the rules that turn it into a save.
 *
 * Kept out of the components so the form stays a picture of this file: what
 * a robot forces, what marking a new item gets, what an in-use item may still
 * send, and which fields are wrong — all answered here and tested in
 * `itemDraft.test.ts`.
 */

/** "New category…" in the category picker. */
export const NEW_CATEGORY = '__new'

export interface Draft {
  readonly name: string
  /** A listed category's name, '' for none, or `NEW_CATEGORY`. */
  readonly category: string
  readonly newCategory: string
  readonly kind: ComponentKind
  readonly tracking: Tracking
  /** Null until somebody picks one: then the default follows the shape (`defaultMarkingFor`). */
  readonly marking: Marking | null
  readonly unit: string
  readonly supplier: string
  readonly partNumber: string
  readonly purchaseLinks: readonly PurchaseLink[]
  readonly leadTimeDays: string
  readonly safetyStock: string
  readonly counters: readonly string[]
  readonly measurements: readonly string[]
  readonly isRoot: boolean
}

/** A new item: bought, with its own serial — what most deliveries are. */
export function blankDraft(name = ''): Draft {
  return {
    name,
    category: '',
    newCategory: '',
    kind: 'buy',
    tracking: 'serialized',
    marking: null,
    unit: '',
    supplier: '',
    partNumber: '',
    purchaseLinks: [],
    leadTimeDays: '',
    safetyStock: '',
    counters: [],
    measurements: [],
    isRoot: false,
  }
}

export function draftOf(type: ComponentType): Draft {
  return {
    name: type.name,
    category: type.category,
    newCategory: '',
    kind: type.kind,
    tracking: type.tracking,
    marking: type.marking,
    unit: type.unit === 'each' ? '' : type.unit,
    supplier: type.supplier ?? '',
    partNumber: type.partNumber ?? '',
    purchaseLinks: [...type.purchaseLinks],
    leadTimeDays: type.leadTimeDays === null ? '' : String(type.leadTimeDays),
    safetyStock: type.safetyStock === null ? '' : String(type.safetyStock),
    counters: [...type.counters],
    measurements: [...type.measurements],
    isRoot: type.isRoot,
  }
}

/**
 * What the item actually is, after the rules the database holds.
 *
 * A robot is built here and has a serial, by definition, and wears its
 * nameplate (§10.1); a counted item has no serial to mark. So ticking "whole
 * robot" answers the questions above it rather than offering a combination the
 * server would refuse.
 */
export function shapeOf(d: Draft): { kind: ComponentKind; tracking: Tracking; marking: Marking } {
  const kind = d.isRoot ? 'make_internal' : d.kind
  const tracking = d.isRoot ? 'serialized' : d.tracking
  const marking: Marking = d.isRoot
    ? 'qr_label'
    : tracking === 'quantity'
      ? 'none'
      : (d.marking ?? defaultMarkingFor({ tracking, kind, isRoot: false }))
  return { kind, tracking, marking }
}

/** Whether the item can be made of other items at all — §4.2: a bought part has no revision of ours. */
export function hasParts(d: Draft): boolean {
  return shapeOf(d).kind !== 'buy'
}

const wholeNumber = (raw: string): number | null => (raw.trim() === '' ? null : Number(raw.trim()))

export interface Problems {
  readonly name?: string | undefined
  readonly newCategory?: string | undefined
  readonly leadTimeDays?: string | undefined
  readonly safetyStock?: string | undefined
  /** Per position, in `flattenParts` order — null where fine. */
  readonly positions?: readonly (string | null)[] | undefined
  readonly parts?: string | undefined
}

/**
 * What is wrong, field by field, in the words shown under each field.
 *
 * `takenNames` is every other item's name: the database refuses a duplicate,
 * and saying so as somebody types beats a 409 after Save.
 */
export function problemsOf(d: Draft, parts: readonly PartsEntry[], takenNames: readonly string[]): Problems {
  const out: { -readonly [K in keyof Problems]: Problems[K] } = {}
  const name = d.name.trim()
  if (name === '') out.name = 'Give it a name'
  else if (takenNames.some((n) => n.trim().toLowerCase() === name.toLowerCase())) out.name = `There is already an item called “${name}”`
  if (d.category === NEW_CATEGORY && d.newCategory.trim() === '') out.newCategory = 'Name the new category'
  const lead = wholeNumber(d.leadTimeDays)
  if (lead !== null && (!Number.isInteger(lead) || lead < 0)) out.leadTimeDays = 'A whole number of days'
  const min = wholeNumber(d.safetyStock)
  if (min !== null && (!Number.isFinite(min) || min < 0)) out.safetyStock = 'A number, zero or more'
  if (hasParts(d)) {
    const names = flattenParts(parts).flatMap((l) => (l.position === null ? [] : [l.position]))
    const issues = positionIssues(names)
    if (issues.some((i) => i !== null)) out.positions = issues
    if (parts.some((p) => p.kind === 'count' && !(p.quantity > 0))) out.parts = 'Every counted part needs an amount above zero'
  }
  return out
}

export function hasProblems(p: Problems): boolean {
  return Object.values(p).some((v) => v !== undefined)
}

/** The body for `createComponentType`, and for `updateComponentType` after `forUpdate`. */
export function payloadOf(d: Draft, category: string) {
  const shape = shapeOf(d)
  return {
    name: d.name.trim(),
    category,
    kind: shape.kind,
    tracking: shape.tracking,
    marking: shape.marking,
    supplier: d.supplier,
    partNumber: d.partNumber,
    unit: shape.tracking === 'quantity' ? d.unit.trim() || 'each' : 'each',
    leadTimeDays: wholeNumber(d.leadTimeDays),
    safetyStock: wholeNumber(d.safetyStock),
    counters: shape.tracking === 'serialized' ? [...d.counters] : [],
    measurements: shape.tracking === 'serialized' ? [...d.measurements] : [],
    isRoot: d.isRoot,
    purchaseLinks: [...d.purchaseLinks],
  }
}

/**
 * An item something already depends on (`reshapable` false) sends everything
 * except the three shape answers, so a rename never trips the server's
 * refusal. The marking is not one of them: it describes the sticker, not the
 * schema, and stays editable.
 */
export function forUpdate(
  payload: ReturnType<typeof payloadOf>,
  existing: ComponentType,
): Partial<ReturnType<typeof payloadOf>> {
  if (existing.reshapable) return payload
  const { kind: _k, tracking: _t, isRoot: _r, ...rest } = payload
  return rest
}

/** The item as it would be, for the preview tree — the stored one with the draft laid over it. */
export function previewType(d: Draft, id: string, existing?: ComponentType): ComponentType {
  const shape = shapeOf(d)
  return {
    ...(existing ?? {
      id,
      category: '',
      supplier: '',
      partNumber: '',
      unit: 'each',
      leadTimeDays: null,
      safetyStock: null,
      counters: [],
      measurements: [],
      instanceCount: 0,
      revisionCount: 0,
      reshapable: true,
      imageId: null,
      structure: null,
      purchaseLinks: [],
    }),
    id,
    name: d.name.trim() || 'New item',
    kind: shape.kind,
    tracking: shape.tracking,
    marking: shape.marking,
    isRoot: d.isRoot,
  }
}
